import { useState, useEffect } from 'react';
import { frandroidAPI, Plan } from '../services/frandroid-api';
import { usePlansContext } from '../contexts';
import { PLAN_TYPES, type PlanType } from '../config/api';

export function usePlans(planType: PlanType = PLAN_TYPES.MOBILE) {
  // Synchronous data source (context, fed by the same data as SSR)
  const contextData = usePlansContext();

  // Data available immediately (no fetch needed)
  const contextPlans = planType === PLAN_TYPES.MOBILE
    ? contextData?.mobilePlans
    : contextData?.internetPlans;

  const availablePlans = contextPlans?.length ? contextPlans : [];

  const hasAvailableData = availablePlans.length > 0;

  // State only for data fetched via the API
  const [fetchedPlans, setFetchedPlans] = useState<Plan[]>([]);
  const [fetching, setFetching] = useState(!hasAvailableData);
  const [error, setError] = useState<Error | null>(null);

  // Derived instead of stored: synchronous context data means nothing is loading.
  const loading = hasAvailableData ? false : fetching;

  useEffect(() => {
    if (hasAvailableData) {
      return;
    }

    let cancelled = false;

    async function fetchData() {
      try {
        const plansData = await frandroidAPI.getPlans(planType);
        if (cancelled) return;
        const sortedPlans = [...plansData].sort((a, b) => b.score - a.score);
        setFetchedPlans(sortedPlans);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        if (!cancelled) setFetching(false);
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [hasAvailableData, planType]);

  // Priority: available data > fetched data
  return {
    plans: hasAvailableData ? availablePlans : fetchedPlans,
    loading,
    error
  };
}
